import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/**
 * @param renderExpanded  Optional `(row) => ReactNode`. When supplied, each row
 *   gets a disclosure toggle and the returned node renders in a full-width row
 *   beneath it. Opt-in: tables that don't pass it are completely unchanged, so
 *   the other admin screens sharing this component keep their current markup.
 * @param expandLabel  Optional `({ row, open }) => string`. Renders a labelled
 *   button instead of a bare chevron — a lone chevron doesn't tell an operator
 *   what it reveals. Falls back to the icon-only control when omitted.
 * @param expandHeader  Column heading for the toggle column. Defaults to
 *   "Details" so the column reads as something rather than as blank space.
 */
export default function DataTable({
  columns,
  rows,
  loading,
  emptyLabel = "No results",
  testid = "data-table",
  renderExpanded,
  expandLabel,
  expandHeader = "Details",
}) {
  const [open, setOpen] = useState({});
  const expandable = typeof renderExpanded === "function";
  const span = columns.length + (expandable ? 1 : 0);
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
          <tr>
            {expandable && (
              expandLabel
                ? <th className="p-3 font-medium whitespace-nowrap">{expandHeader}</th>
                : <th className="p-3 w-8" aria-label="Expand" />
            )}
            {columns.map((c) => (
              <th key={c.key} className="p-3 font-medium whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={span} className="p-6 text-center text-[hsl(var(--kb-muted))]">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={span} className="p-6 text-center text-[hsl(var(--kb-muted))]">{emptyLabel}</td></tr>
          ) : (
            rows.map((r, i) => {
              const key = r.id || i;
              const isOpen = !!open[key];
              return (
                <React.Fragment key={key}>
                  <tr className="border-t border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]/50">
                    {expandable && (
                      <td className="p-3 align-top">
                        <button
                          type="button"
                          onClick={() => toggle(key)}
                          aria-expanded={isOpen}
                          aria-controls={`${testid}-details-${key}`}
                          aria-label={isOpen ? "Hide details" : "Show details"}
                          className={
                            expandLabel
                              // Same compact outline button the pagination
                              // controls use, so this reads as part of the
                              // existing system. min-h keeps the tap target
                              // comfortable on a phone.
                              ? "kb-btn-outline !py-2 !px-3 !text-[11px] inline-flex items-center gap-1.5 whitespace-nowrap min-h-[40px]"
                              : "p-1 -m-1 text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
                          }
                          data-testid={`${testid}-expand-${key}`}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          {expandLabel ? expandLabel({ row: r, open: isOpen }) : null}
                        </button>
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key} className="p-3 align-top">{c.render ? c.render(r) : r[c.key]}</td>
                    ))}
                  </tr>
                  {expandable && isOpen && (
                    <tr className="border-t border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))]/40">
                      <td colSpan={span} className="p-0" data-testid={`${testid}-details-${key}`}>
                        {renderExpanded(r)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({ page, limit, total, onPage, testid = "pagination" }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm" data-testid={testid}>
      <div className="text-[hsl(var(--kb-muted))]">
        Page {page} of {pages} · {total} results
      </div>
      <div className="flex gap-2">
        <button
          className="kb-btn-outline !py-2 !px-3 !text-xs"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid={`${testid}-prev`}
        >
          Prev
        </button>
        <button
          className="kb-btn-outline !py-2 !px-3 !text-xs"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          data-testid={`${testid}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
